import React, { useMemo, useState } from "react";
import Accordion from "@components/Accordion/Accordion";
import { calculateShipAge } from "@features/vessel-call/vessel-call-status/utils";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type Company } from "@services/vessel-call/types";
import type { Vessel } from "@services/vessel/types";
import { capitalizeWord } from "@utils/string";
import { useTranslation } from "react-i18next";

interface VesselDetailsProps {
  testId: string;
  langBase: string;
  vesselDetails: Vessel | undefined;
}

const SingleDetail: React.FC<{ label: string; value: string | number | undefined }> = ({
  label,
  value,
}) => {
  return (
    <div className="flex flex-col min-w-0">
      <dt className="text-sm text-slate-700">{label}</dt>
      <dd className="mt-0.5 wrap-break-word text-sm font-bold text-slate-900">{value ?? "—"}</dd>
    </div>
  );
};

// @TODO - Status, agency, and vessel condition should be added after the vessel call creation is completed

const VesselDetails: React.FC<VesselDetailsProps> = ({ testId, langBase, vesselDetails }) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(true);

  const { options: companies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    "bff-vessel-call",
  );

  const agencyName = useMemo(() => {
    return companies.find((c) => c.companyCode === user?.companyId)?.companyNameEnglish;
  }, [companies, user]);

  const flag = useMemo(() => {
    return `${vesselDetails?.country?.countryCode ?? ""}, ${capitalizeWord(vesselDetails?.country?.countryName ?? "")}`;
  }, [vesselDetails]);

  const columns = [
    [{ label: t(`${langBase}.vessel-location`), value: "Sea" }],
    [
      { label: t(`${langBase}.imo-number`), value: vesselDetails?.vesselImoNumber },
      { label: t(`${langBase}.vessel-name`), value: vesselDetails?.vesselName },
      {
        label: t(`${langBase}.type`),
        value: vesselDetails?.vesselType?.vesselTypeDesc?.toUpperCase(),
      },
    ],
    [
      { label: t(`${langBase}.flag`), value: flag },
      ...(agencyName
        ? [{ label: t(`${langBase}.agency`), value: capitalizeWord(agencyName) }]
        : []),
      {
        label: t(`${langBase}.age`),
        value: calculateShipAge(vesselDetails?.dateOfDelivery, t),
      },
    ],
    [
      { label: t(`${langBase}.loa`), value: vesselDetails?.loa },
      { label: t(`${langBase}.ship-beam`), value: vesselDetails?.beam },
      { label: t(`${langBase}.ship-max-draft`), value: vesselDetails?.draftMax },
    ],
    [
      {
        label: t(`${langBase}.gross-tonnage`),
        value: vesselDetails?.grt,
      },
    ],
  ];

  return (
    <Accordion
      testId={`${testId}-vessel-details`}
      summary={t(`${langBase}.title`)}
      expanded={isOpen}
      onChange={() => setIsOpen(!isOpen)}
      isTransparent={true}
    >
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:gap-y-10 sm:-mx-4">
        {columns.map((col, idx) => (
          <div
            key={idx}
            className={`w-full sm:w-1/2 lg:w-1/5 sm:px-4 ${
              idx !== 0 ? "lg:pl-8 mt-8 sm:mt-0" : "lg:border-r border-slate-200 lg:pr-8"
            }`}
          >
            <div className="space-y-6">
              {col.map((item) => (
                <SingleDetail key={item.label} label={item.label} value={item.value} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Accordion>
  );
};

export default VesselDetails;
